import React from 'react';
import { StyleSheet, TouchableOpacity, type TouchableOpacityProps, View } from 'react-native';

import { theme } from '@garnish/constants';

import { Icon } from '../../../../../Icon';
import { BodyText } from '../../../../../Text';
import { NAVBAR_FLOATING_ACTION_SIZE } from '../../../../Navbar.constants';
import { NavbarGlassView } from '../NavbarGlassView/NavbarGlassView';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarFloatingLocationButton = (props: Props) => {
  const { disabled, eyebrow, onPress, shadow, title, ...restProps } = props;

  // ─────────────────────────────────────────────────────────────────────

  if (disabled) {
    return (
      <View style={styles.outerContainer}>
        <LocationButtonContent eyebrow={eyebrow} shadow={shadow} title={title} />
      </View>
    );
  }

  return (
    <TouchableOpacity
      {...restProps}
      accessibilityRole="link"
      activeOpacity={0.7}
      onPress={onPress}
      style={styles.outerContainer}
    >
      <LocationButtonContent eyebrow={eyebrow} shadow={shadow} title={title} />
    </TouchableOpacity>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const LocationButtonContent = (props: ContentProps) => {
  const { eyebrow, shadow, title } = props;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <NavbarGlassView canHover shadow={shadow} style={styles.container}>
      <View style={styles.contentContainer}>
        {eyebrow ? (
          <BodyText
            maxFontSizeMultiplier={1.2}
            numberOfLines={1}
            sizeMatch={['16']}
            style={styles.eyebrow}
          >
            {eyebrow}
          </BodyText>
        ) : undefined}

        <View style={styles.titleContainer}>
          <BodyText
            maxFontSizeMultiplier={1.2}
            numberOfLines={1}
            sizeMatch={['16']}
            style={eyebrow ? styles.selectedLabel : styles.label}
          >
            {title}
          </BodyText>

          <View style={styles.iconContainer}>
            <Icon name="IconCaretDown" width={14} height={14} color={theme.colors.KALE} />
          </View>
        </View>
      </View>
    </NavbarGlassView>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainer: {
    flexShrink: 1,
    maxWidth: 500,
  },
  container: {
    height: NAVBAR_FLOATING_ACTION_SIZE,
    maxWidth: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing['1'],
    paddingHorizontal: theme.spacing['6'],
  },
  contentContainer: {
    flexShrink: 1,
    minWidth: 0,
    maxWidth: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['1'],
  },
  titleContainer: {
    flexShrink: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['1'],
  },
  iconContainer: {
    paddingTop: 3, // to better align with title
  },
  eyebrow: {
    flexShrink: 0,
    color: theme.colors.OPACITY.DARK_KALE.DARKER,
  },
  label: {
    flexShrink: 1,
    minWidth: 0,
    color: theme.colors.CHARCOAL,
    textAlign: 'right',
  },
  selectedLabel: {
    flexShrink: 1,
    minWidth: 0,
    color: theme.colors.KALE,
    textAlign: 'right',
    textDecorationLine: 'underline',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ContentProps = {
  eyebrow?: string;
  shadow?: boolean;
  title: string;
};

type Props = ContentProps & Pick<TouchableOpacityProps, 'aria-label' | 'disabled' | 'onPress'>;
